/**
 * Threshold Administration Workspace · P0.
 *
 * Single authoritative page for tuning purchasing engine configuration.
 * Reads a consolidated snapshot from `/api/thresholds` and PATCHes back
 * a partial payload on Save. Every value carries "Current · Description
 * · Business Impact · Last Updated · Updated By".
 *
 * The recommendation engine is not touched by this file. This is an
 * administrative UX layer over the existing configuration collections.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sliders, Info, WarningCircle, ClockCounterClockwise, FloppyDisk,
  CheckCircle, ArrowsClockwise, Lock,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PRODUCT_LABEL = {
  regular:      "Regular Gasoline",
  premium:      "Premium Gasoline",
  clear_diesel: "Clear Diesel",
  dyed_diesel:  "Dyed Diesel",
  def:          "DEF",
};

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/* ==================================================================
   Root
   ================================================================== */
export default function ThresholdAdministration() {
  const { user } = useAuth();
  const canWrite = user?.role === "Administrator" || user?.role === "Owner";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [audit, setAudit] = useState([]);

  // Editable draft — mirrors snapshot but is what the user can mutate.
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/thresholds"),
        api.get("/thresholds/audit?limit=20").catch(() => ({ data: { items: [] } })),
      ]);
      setSnapshot(s.data);
      setAudit(a.data.items || []);
      setDraft(_toDraft(s.data));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => _diff(snapshot, draft), [snapshot, draft]);
  const dirtyCount = dirty ? _countChanges(dirty) : 0;

  const save = useCallback(async () => {
    if (!dirty || dirtyCount === 0) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/thresholds", dirty);
      toast.success(`Threshold configuration saved · ${data.changes} change${data.changes === 1 ? "" : "s"} audited.`);
      setSnapshot(data.config);
      setDraft(_toDraft(data.config));
      // Refresh audit list.
      const a = await api.get("/thresholds/audit?limit=20");
      setAudit(a.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save configuration.");
    } finally {
      setSaving(false);
    }
  }, [dirty, dirtyCount]);

  if (loading || !snapshot) {
    return <div className="p-8 text-sm text-slate-500">Loading threshold configuration…</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto pb-28" data-testid="ta-page">
      {/* Header ------------------------------------------------ */}
      <header className="border-b border-slate-200 pb-4 mb-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <Sliders size={22} weight="duotone" className="text-slate-700" />
          <h1 className="text-2xl font-semibold text-slate-900">Threshold Administration</h1>
          <span className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1"
                data-testid="ta-role-badge">
            <Lock size={11} /> {canWrite ? user?.role : "Read-only"}
          </span>
        </div>
        <p className="text-sm text-slate-600 mt-1.5" data-testid="ta-philosophy">
          Single source of truth for purchasing engine policy. Every value is business configuration,
          not code — every change is auditable, and takes effect immediately without a restart.
        </p>
      </header>

      {/* DEC-054 · RC1 M2 · explicit read-only banner for viewers */}
      {!canWrite ? (
        <div data-testid="ta-readonly-banner"
             className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 inline-flex items-center gap-2">
          <Lock size={13} weight="fill" />
          You are viewing threshold configuration in read-only mode. Only Administrator or Owner roles can save changes.
        </div>
      ) : null}

      {/* 1 · Purchasing Engine · read-only summary --------------- */}
      <PurchasingEngineCard state={snapshot.purchasing_engine} />

      {/* 2 · Confidence Bands ------------------------------------ */}
      <ConfidenceBandsCard
        state={draft.confidence_bands}
        base={snapshot.confidence_bands}
        impact={snapshot.business_impact.confidence_bands}
        canWrite={canWrite}
        onChange={(patch) => setDraft((d) => ({
          ...d, confidence_bands: { ...d.confidence_bands, ...patch }
        }))}
      />

      {/* 3 · Days of Cover --------------------------------------- */}
      <DaysOfCoverCard
        state={draft.days_of_cover}
        base={snapshot.days_of_cover}
        impact={snapshot.business_impact.days_of_cover}
        canWrite={canWrite}
        onChangeHorizon={(v) => setDraft((d) => ({
          ...d, days_of_cover: { ...d.days_of_cover, consumption_horizon_days: v }
        }))}
        onChangeProduct={(code, key, value) => setDraft((d) => ({
          ...d,
          days_of_cover: {
            ...d.days_of_cover,
            products: d.days_of_cover.products.map((p) =>
              p.product_code === code ? { ...p, [key]: value } : p
            ),
          },
        }))}
      />

      {/* 4 · Margin Thresholds ----------------------------------- */}
      <MarginThresholdsCard
        state={draft.margin_thresholds}
        base={snapshot.margin_thresholds}
        impact={snapshot.business_impact.margin_thresholds}
        canWrite={canWrite}
        onChange={(patch) => setDraft((d) => ({
          ...d, margin_thresholds: { ...d.margin_thresholds, ...patch }
        }))}
      />

      {/* 5 · Reconciliation Variance · Sprint 3A ------------------ */}
      <ReconciliationVarianceCard
        state={draft.reconciliation_variance}
        base={snapshot.reconciliation_variance}
        impact={snapshot.business_impact.reconciliation_variance}
        canWrite={canWrite}
        onChange={(patch) => setDraft((d) => ({
          ...d, reconciliation_variance: { ...d.reconciliation_variance, ...patch }
        }))}
      />

      {/* Reserved Future ---------------------------------------- */}
      <ReservedFutureCard reserved={snapshot.reserved} />

      {/* Audit Trail -------------------------------------------- */}
      <AuditTrail items={audit} />

      {/* Sticky footer ------------------------------------------ */}
      <div className="fixed bottom-0 inset-x-0 lg:left-64 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-6 py-3 flex items-center gap-4"
           data-testid="ta-footer">
        <div className="text-xs text-slate-600" data-testid="ta-dirty-count">
          {dirtyCount === 0
            ? "No pending changes."
            : <><b>{dirtyCount}</b> pending change{dirtyCount === 1 ? "" : "s"} · takes effect immediately after save</>}
        </div>
        <button data-testid="ta-refresh"
                onClick={load}
                className="ml-auto text-xs font-medium px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
          <ArrowsClockwise size={12} /> Reload
        </button>
        <button data-testid="ta-save"
                onClick={save}
                disabled={!canWrite || saving || dirtyCount === 0}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 shadow-sm">
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Saving…" : "Save Threshold Configuration"}
        </button>
      </div>
    </div>
  );
}

/* ==================================================================
   Section cards
   ================================================================== */
function SectionCard({ title, subtitle, testId, children, headerRight, tone = "default" }) {
  const border = tone === "reserved"
    ? "border-dashed border-slate-300 bg-slate-50/60"
    : "border-slate-200 bg-white";
  return (
    <section data-testid={testId}
             className={`rounded-lg border ${border} mb-5 overflow-hidden`}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-baseline gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="text-xs text-slate-500 italic flex-1 min-w-0">{subtitle}</p> : null}
        {headerRight}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function FieldRow({ label, description, impact, currentDisplay, lastUpdatedBy, lastUpdatedAt, children, testId }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 py-3 border-b border-slate-100 last:border-b-0"
         data-testid={testId}>
      <div className="md:col-span-4">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {description ? <p className="text-xs text-slate-600 mt-0.5">{description}</p> : null}
        {impact ? (
          <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
            <Info size={11} className="mt-0.5 shrink-0" />
            <span><b>Impact:</b> {impact}</span>
          </p>
        ) : null}
      </div>
      <div className="md:col-span-3 flex items-center gap-2">
        {children}
      </div>
      <div className="md:col-span-3 text-xs text-slate-500">
        <div><span className="text-slate-400">Current:</span> <b className="text-slate-800 tabular-nums">{currentDisplay}</b></div>
      </div>
      <div className="md:col-span-2 text-[11px] text-slate-500 leading-relaxed">
        <div>Last updated {fmtDT(lastUpdatedAt)}</div>
        <div className="truncate">by {lastUpdatedBy || "—"}</div>
      </div>
    </div>
  );
}

function NumberInput({ value, onChange, min, max, step = 1, disabled, testId, placeholder }) {
  return (
    <input
      type="number"
      data-testid={testId}
      value={value === null || value === undefined ? "" : value}
      min={min} max={max} step={step}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : Number(v));
      }}
      className="w-24 text-sm rounded-md border border-slate-300 px-2 py-1 tabular-nums focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:bg-slate-100"
    />
  );
}

/* ------------------------------ Purchasing Engine ---------------- */
function PurchasingEngineCard({ state }) {
  const brief = state.last_morning_brief;
  return (
    <SectionCard title="Purchasing Engine" testId="ta-section-purchasing-engine"
                 subtitle="Read-only operational summary."
                 headerRight={<span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600">Informational</span>}>
      <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Engine version</dt>
          <dd className="mt-0.5 font-semibold text-slate-900" data-testid="ta-engine-version">{state.version}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Advisory mode</dt>
          <dd className="mt-0.5 inline-flex items-center gap-1 font-semibold text-emerald-800" data-testid="ta-engine-mode">
            <CheckCircle size={12} weight="fill" /> Advisory only
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Vocabulary</dt>
          <dd className="mt-0.5 flex gap-1.5" data-testid="ta-engine-vocabulary">
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">ORDER</span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-900 border border-sky-300">WATCH</span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300">WAIT</span>
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Last recommendation refresh</dt>
          <dd className="mt-0.5 text-slate-800 tabular-nums" data-testid="ta-engine-refresh">
            {fmtDT(state.last_recommendation_refresh)}
          </dd>
        </div>
        <div className="md:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-slate-500">Last Morning Brief prepared</dt>
          <dd className="mt-0.5 text-slate-800" data-testid="ta-engine-last-brief">
            {brief ? <>{brief.date} at {new Date(brief.prepared_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · by <b>{brief.prepared_by}</b></> : "Not prepared yet today."}
          </dd>
        </div>
      </dl>
    </SectionCard>
  );
}

/* ------------------------------ Confidence Bands ---------------- */
function ConfidenceBandsCard({ state, base, impact, canWrite, onChange }) {
  return (
    <SectionCard title="Confidence Bands" testId="ta-section-confidence-bands"
                 subtitle="Translates numeric 0–100 confidence into HIGH · MEDIUM · LOW labels for presentation.">
      <FieldRow
        label="HIGH threshold (high_min)"
        description="Numeric confidence at or above this displays as HIGH."
        impact={impact.high_min}
        currentDisplay={base.high_min ?? "not set"}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-high-min"
      >
        <NumberInput value={state.high_min} min={0} max={100} disabled={!canWrite}
                     placeholder="e.g. 75"
                     onChange={(v) => onChange({ high_min: v })}
                     testId="ta-input-high-min" />
        <span className="text-xs text-slate-500">of 100</span>
      </FieldRow>
      <FieldRow
        label="MEDIUM threshold (medium_min)"
        description="At or above this displays MEDIUM · below drops to LOW."
        impact={impact.medium_min}
        currentDisplay={base.medium_min ?? "not set"}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-medium-min"
      >
        <NumberInput value={state.medium_min} min={0} max={100} disabled={!canWrite}
                     placeholder="e.g. 50"
                     onChange={(v) => onChange({ medium_min: v })}
                     testId="ta-input-medium-min" />
        <span className="text-xs text-slate-500">of 100</span>
      </FieldRow>
      {(state.high_min !== null && state.medium_min !== null && state.medium_min > state.high_min) ? (
        <p className="text-xs text-rose-700 mt-2 inline-flex items-center gap-1">
          <WarningCircle size={12} weight="fill" /> MEDIUM threshold cannot exceed HIGH threshold.
        </p>
      ) : null}
    </SectionCard>
  );
}

/* ------------------------------ Days of Cover ------------------- */
function DaysOfCoverCard({ state, base, impact, canWrite, onChangeHorizon, onChangeProduct }) {
  return (
    <SectionCard title="Days of Cover" testId="ta-section-days-of-cover"
                 subtitle="Inventory tone thresholds and consumption horizon.">
      <FieldRow
        label="Consumption horizon (days)"
        description="Historical window used to derive daily consumption rate."
        impact={impact.consumption_horizon_days}
        currentDisplay={base.consumption_horizon_days ?? "engine default"}
        lastUpdatedAt={null} lastUpdatedBy={null}
        testId="ta-field-horizon"
      >
        <NumberInput value={state.consumption_horizon_days} min={1} max={90} disabled={!canWrite}
                     placeholder="e.g. 14"
                     onChange={(v) => onChangeHorizon(v)}
                     testId="ta-input-horizon" />
        <span className="text-xs text-slate-500">days</span>
      </FieldRow>

      <div className="mt-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Per-product thresholds</h3>
        <p className="text-xs text-slate-500 mb-2">
          <span className="text-emerald-700 font-semibold">Green</span> at or above green_min_days ·
          <span className="text-rose-700 font-semibold"> Red</span> at or below red_max_days.
        </p>
        <div className="rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="ta-doc-table">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">🟢 Green min</th>
                <th className="text-left px-3 py-2">🔴 Red max</th>
                <th className="text-left px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {state.products.map((p) => (
                <tr key={p.product_code} data-testid={`ta-doc-row-${p.product_code}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{PRODUCT_LABEL[p.product_code] || p.product_code}</td>
                  <td className="px-3 py-2">
                    <NumberInput value={p.green_min_days} min={0.5} step={0.5} disabled={!canWrite}
                                 onChange={(v) => onChangeProduct(p.product_code, "green_min_days", v)}
                                 testId={`ta-doc-green-${p.product_code}`} />
                  </td>
                  <td className="px-3 py-2">
                    <NumberInput value={p.red_max_days} min={0.5} step={0.5} disabled={!canWrite}
                                 onChange={(v) => onChangeProduct(p.product_code, "red_max_days", v)}
                                 testId={`ta-doc-red-${p.product_code}`} />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {p.updated_at ? <>{fmtDT(p.updated_at)}<br /><span className="text-slate-400">by</span> {p.updated_by}</> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

/* ------------------------------ Margin Thresholds -------------- */
function MarginThresholdsCard({ state, base, impact, canWrite, onChange }) {
  return (
    <SectionCard title="Margin Thresholds" testId="ta-section-margin"
                 subtitle="Purchasing sensitivity around margin. Policy values used by the engine.">
      <FieldRow
        label="Minimum acceptable margin"
        description="Floor margin the platform treats as acceptable."
        impact={impact.minimum_margin_pct}
        currentDisplay={base.minimum_margin_pct === null || base.minimum_margin_pct === undefined ? "not set" : `${base.minimum_margin_pct}%`}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-margin-min"
      >
        <NumberInput value={state.minimum_margin_pct} min={0} max={100} step={0.1} disabled={!canWrite}
                     placeholder="e.g. 3.0"
                     onChange={(v) => onChange({ minimum_margin_pct: v })}
                     testId="ta-input-margin-min" />
        <span className="text-xs text-slate-500">%</span>
      </FieldRow>
      <FieldRow
        label="Preferred margin"
        description="Target margin the platform aims for."
        impact={impact.preferred_margin_pct}
        currentDisplay={base.preferred_margin_pct === null || base.preferred_margin_pct === undefined ? "not set" : `${base.preferred_margin_pct}%`}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-margin-preferred"
      >
        <NumberInput value={state.preferred_margin_pct} min={0} max={100} step={0.1} disabled={!canWrite}
                     placeholder="e.g. 8.0"
                     onChange={(v) => onChange({ preferred_margin_pct: v })}
                     testId="ta-input-margin-preferred" />
        <span className="text-xs text-slate-500">%</span>
      </FieldRow>
      <FieldRow
        label="Aggressive opportunity threshold"
        description="Above this margin, purchasing signals surface as high-value opportunities."
        impact={impact.aggressive_opportunity_margin_pct}
        currentDisplay={base.aggressive_opportunity_margin_pct === null || base.aggressive_opportunity_margin_pct === undefined ? "not set" : `${base.aggressive_opportunity_margin_pct}%`}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-margin-aggressive"
      >
        <NumberInput value={state.aggressive_opportunity_margin_pct} min={0} max={100} step={0.1} disabled={!canWrite}
                     placeholder="e.g. 15.0"
                     onChange={(v) => onChange({ aggressive_opportunity_margin_pct: v })}
                     testId="ta-input-margin-aggressive" />
        <span className="text-xs text-slate-500">%</span>
      </FieldRow>
    </SectionCard>
  );
}

/* ------------------------------ Reconciliation Variance · Sprint 3A -- */
function ReconciliationVarianceCard({ state, base, impact, canWrite, onChange }) {
  return (
    <SectionCard
      title="Reconciliation Variance"
      testId="ta-section-variance"
      subtitle="Petroleum reconciliation status thresholds. Applied by the Reconciliation engine every morning."
    >
      <FieldRow
        label="Green (within tolerance) — variance ≤"
        description="Below this variance, reconciliation is 🟢 and requires no investigation notes."
        impact={impact.green_max_pct}
        currentDisplay={base.green_max_pct === null || base.green_max_pct === undefined ? "not set" : `${base.green_max_pct}%`}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-variance-green"
      >
        <NumberInput
          value={state.green_max_pct}
          min={0}
          max={100}
          step={0.1}
          disabled={!canWrite}
          placeholder="e.g. 0.5"
          onChange={(v) => onChange({ green_max_pct: v })}
          testId="ta-input-variance-green"
        />
        <span className="text-xs text-slate-500">%</span>
      </FieldRow>
      <FieldRow
        label="Yellow (investigate) — variance ≤"
        description="Above green but at or below this variance is 🟡 (investigation notes required). Above this becomes 🔴 escalate."
        impact={impact.yellow_max_pct}
        currentDisplay={base.yellow_max_pct === null || base.yellow_max_pct === undefined ? "not set" : `${base.yellow_max_pct}%`}
        lastUpdatedAt={base.updated_at}
        lastUpdatedBy={base.updated_by}
        testId="ta-field-variance-yellow"
      >
        <NumberInput
          value={state.yellow_max_pct}
          min={0}
          max={100}
          step={0.1}
          disabled={!canWrite}
          placeholder="e.g. 2.0"
          onChange={(v) => onChange({ yellow_max_pct: v })}
          testId="ta-input-variance-yellow"
        />
        <span className="text-xs text-slate-500">%</span>
      </FieldRow>
    </SectionCard>
  );
}

/* ------------------------------ Reserved Future ---------------- */
function ReservedFutureCard({ reserved }) {
  const items = [
    { key: "purchasing_sensitivity",   label: "Purchasing Sensitivity",  note: "Conservative · Balanced · Aggressive policy modes." },
    { key: "supplier_priority",        label: "Supplier Priority Policies", note: "Preferred supplier hierarchies and allocation rules." },
    { key: "seasonal_purchasing",      label: "Seasonal Purchasing Policies", note: "Winter / summer demand overlays." },
    { key: "inventory_health_rules",   label: "Inventory Health Rules",  note: "Cross-product operational safeguards." },
  ];
  return (
    <SectionCard title="Reserved for Future Sprints" testId="ta-section-reserved" tone="reserved"
                 subtitle="Architectural space for policy areas we have committed to build later.">
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <li key={it.key} className="rounded border border-slate-200 bg-white px-3 py-2 text-sm"
              data-testid={`ta-reserved-${it.key}`}>
            <div className="flex items-center gap-2">
              <span className="text-slate-900 font-semibold">{it.label}</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600">Coming later</span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{it.note}</p>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

/* ------------------------------ Audit Trail --------------------- */
function AuditTrail({ items }) {
  return (
    <SectionCard title="Recent Configuration Changes" testId="ta-section-audit"
                 subtitle="Every change captures previous value, new value, updated by, updated at."
                 headerRight={<span className="text-xs text-slate-500">{items.length} row{items.length === 1 ? "" : "s"}</span>}>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="ta-audit-empty">No configuration changes recorded yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="ta-audit-list">
          {items.map((r) => (
            <li key={r.id} className="py-2 flex items-start gap-3 text-sm"
                data-testid={`ta-audit-row-${r.id}`}>
              <ClockCounterClockwise size={14} className="mt-0.5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-[11px] uppercase tracking-wide text-slate-500">{r.section}</span>
                  <span className="text-slate-800 font-semibold">{r.key}{r.product_code ? ` · ${r.product_code}` : ""}</span>
                </div>
                <p className="text-slate-700">
                  <span className="text-slate-400">from</span>{" "}
                  <b className="text-slate-900 tabular-nums">{String(r.previous_value ?? "—")}</b>
                  <span className="text-slate-400"> to</span>{" "}
                  <b className="text-slate-900 tabular-nums">{String(r.new_value ?? "—")}</b>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {r.updated_by} · {fmtDT(r.updated_at)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/* ==================================================================
   Draft helpers
   ================================================================== */
function _toDraft(snap) {
  return {
    confidence_bands: {
      high_min:   snap.confidence_bands.high_min ?? null,
      medium_min: snap.confidence_bands.medium_min ?? null,
    },
    days_of_cover: {
      consumption_horizon_days: snap.days_of_cover.consumption_horizon_days ?? null,
      products: (snap.days_of_cover.products || []).map((p) => ({
        product_code:   p.product_code,
        green_min_days: p.green_min_days ?? null,
        red_max_days:   p.red_max_days ?? null,
      })),
    },
    margin_thresholds: {
      minimum_margin_pct:                snap.margin_thresholds.minimum_margin_pct ?? null,
      preferred_margin_pct:              snap.margin_thresholds.preferred_margin_pct ?? null,
      aggressive_opportunity_margin_pct: snap.margin_thresholds.aggressive_opportunity_margin_pct ?? null,
    },
    reconciliation_variance: {
      green_max_pct:  snap.reconciliation_variance?.green_max_pct  ?? null,
      yellow_max_pct: snap.reconciliation_variance?.yellow_max_pct ?? null,
    },
  };
}

function _diff(snap, draft) {
  if (!snap || !draft) return null;
  const out = {};
  // Confidence bands · include the whole block if either value changed.
  const cb = draft.confidence_bands;
  if (cb.high_min !== (snap.confidence_bands.high_min ?? null) ||
      cb.medium_min !== (snap.confidence_bands.medium_min ?? null)) {
    out.confidence_bands = { high_min: cb.high_min, medium_min: cb.medium_min };
  }
  // Days of cover
  const doc = draft.days_of_cover;
  const docPatch = {};
  if (doc.consumption_horizon_days !== (snap.days_of_cover.consumption_horizon_days ?? null)) {
    docPatch.consumption_horizon_days = doc.consumption_horizon_days;
  }
  const changedProducts = doc.products.filter((p) => {
    const base = snap.days_of_cover.products.find((x) => x.product_code === p.product_code) || {};
    return (p.green_min_days !== (base.green_min_days ?? null))
        || (p.red_max_days   !== (base.red_max_days   ?? null));
  }).filter((p) => p.green_min_days !== null && p.red_max_days !== null);
  if (changedProducts.length) docPatch.products = changedProducts;
  if (Object.keys(docPatch).length) out.days_of_cover = docPatch;
  // Margin
  const m = draft.margin_thresholds;
  const mPatch = {};
  for (const k of ["minimum_margin_pct", "preferred_margin_pct", "aggressive_opportunity_margin_pct"]) {
    if (m[k] !== (snap.margin_thresholds[k] ?? null)) mPatch[k] = m[k];
  }
  if (Object.keys(mPatch).length) out.margin_thresholds = mPatch;
  // Reconciliation Variance · Sprint 3A
  const rv = draft.reconciliation_variance;
  const rvPatch = {};
  for (const k of ["green_max_pct", "yellow_max_pct"]) {
    if (rv[k] !== (snap.reconciliation_variance?.[k] ?? null)) rvPatch[k] = rv[k];
  }
  if (Object.keys(rvPatch).length) out.reconciliation_variance = rvPatch;
  return Object.keys(out).length ? out : null;
}

function _countChanges(diff) {
  let n = 0;
  if (diff.confidence_bands) n += Object.keys(diff.confidence_bands).length;
  if (diff.days_of_cover) {
    if (diff.days_of_cover.consumption_horizon_days !== undefined) n++;
    if (diff.days_of_cover.products) n += diff.days_of_cover.products.length * 2;
  }
  if (diff.margin_thresholds) n += Object.keys(diff.margin_thresholds).length;
  if (diff.reconciliation_variance) n += Object.keys(diff.reconciliation_variance).length;
  return n;
}
